import { suiteRoot } from "./auth";

export type CoffreEntry = { name: string; path: string; kind: string; size?: number | null };

export type CoffreSpot = { path: string; ownerId: number; shareRoot?: boolean };

export type NoteLabel = string;
export type CrmNote = { id: string; heading: string; body: string; label: NoteLabel; author: string; authorId?: number; taskId?: string; fromTask?: boolean };

type ListHit = { at: number; rows: CoffreEntry[] };
type NotesHit = { at: number; notes: CrmNote[]; exists: boolean; size: number | null };

export class CoffreDenied extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "CoffreDenied";
    this.code = code;
  }
}

export function coffreLocked(error: unknown): boolean {
  return error instanceof CoffreDenied && (error.code === "coffre_locked" || error.code === "coffre_session_expired");
}

const unlockWaiters = new Set<() => void>();
let unlockAsked = false;

export function onCoffreUnlockAsk(listen: () => void): () => void {
  unlockWaiters.add(listen);
  if (unlockAsked) listen();
  return () => { unlockWaiters.delete(listen); };
}

export function requestCoffreUnlock(): void {
  unlockAsked = true;
  unlockWaiters.forEach((listen) => listen());
}

export function coffreUnlockClosed(): void {
  unlockAsked = false;
}

export async function unlockCoffre(password: string): Promise<void> {
  const res = await fetch(`${suiteRoot()}/api_suite/coffre-unlock.php`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password, session_extend_seconds: coffreSessionSeconds() }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; message?: string };
  if (!res.ok || data.success === false) {
    throw new CoffreDenied(data.error || "invalid_password", data.message || "Code incorrect.");
  }
  unlockAsked = false;
  window.dispatchEvent(new Event("crm-coffre-unlocked"));
}

function coffreSessionSeconds(): number {
  try {
    const raw = localStorage.getItem("coffre_session_extend");
    if (!raw) return 3600;
    const idle = Number((JSON.parse(raw) as { idleSeconds?: number }).idleSeconds);
    if (idle === 3600 || idle === 86400 || idle === 604800) return idle;
  } catch {
    /* durée par défaut : une heure, comme le Coffre */
  }
  return 3600;
}

function raiseCoffre(status: number, error?: string, message?: string): never {
  const code = error || "";
  if (code === "coffre_locked" || code === "coffre_session_expired") requestCoffreUnlock();
  throw new CoffreDenied(code, coffreError(status, error, message));
}

const LIST_TTL_MS = 3 * 60 * 1000;
const NOTES_TTL_MS = 3 * 60 * 1000;
const LIST_SESSION = "crm.cfl.v1.";
const NOTES_SESSION = "crm.cfn.v1.";
const listHits = new Map<string, ListHit>();
const listInflight = new Map<string, Promise<CoffreEntry[]>>();
const notesHits = new Map<string, NotesHit>();
const notesInflight = new Map<string, Promise<{ notes: CrmNote[]; exists: boolean }>>();

const MUTATING_COFFRE = new Set([
  "coffre-delete.php",
  "coffre-move.php",
  "coffre-rename.php",
  "coffre-mkdir.php",
  "coffre-create-file.php",
  "coffre-save-text.php",
  "coffre-upload.php",
]);

export function coffreZone(ownerId: number): Record<string, string> {
  return ownerId > 0 ? { zone: "shared", owner_id: String(ownerId) } : { zone: "vault" };
}

function listCacheKey(path: string, foldersOnly: boolean, ownerId: number): string {
  return `${ownerId}|${foldersOnly ? "1" : "0"}|${path}`;
}

function notesCacheKey(folder: string, ownerId: number): string {
  return `${ownerId}|notes|${folder}`;
}

/** Invalide le cache listing / notes (après écriture, refresh manuel, etc.). */
export function dropCoffreCache(path?: string, ownerId?: number): void {
  if (path === undefined) {
    listHits.clear();
    listInflight.clear();
    notesHits.clear();
    notesInflight.clear();
    dropSessionPrefix(LIST_SESSION);
    dropSessionPrefix(NOTES_SESSION);
    return;
  }
  const owner = String(ownerId ?? 0);
  for (const key of [...listHits.keys()]) {
    if (key.startsWith(`${owner}|`)) listHits.delete(key);
  }
  for (const key of [...listInflight.keys()]) {
    if (key.startsWith(`${owner}|`)) listInflight.delete(key);
  }
  for (const key of [...notesHits.keys()]) {
    if (key.startsWith(`${owner}|`)) notesHits.delete(key);
  }
  for (const key of [...notesInflight.keys()]) {
    if (key.startsWith(`${owner}|`)) notesInflight.delete(key);
  }
  dropSessionPrefix(`${LIST_SESSION}${owner}|`);
  dropSessionPrefix(`${NOTES_SESSION}${owner}|`);
}

export function peekCoffreList(path: string, foldersOnly: boolean, ownerId = 0): CoffreEntry[] | null {
  const key = listCacheKey(path, foldersOnly, ownerId);
  const hit = listHits.get(key) || readSession<ListHit>(`${LIST_SESSION}${key}`);
  return hit ? hit.rows.map((row) => ({ ...row })) : null;
}

export function peekFicheNotes(folder: string, ownerId = 0): { notes: CrmNote[]; exists: boolean } | null {
  const key = notesCacheKey(folder, ownerId);
  const hit = notesHits.get(key) || readSession<NotesHit>(`${NOTES_SESSION}${key}`);
  if (!hit) return null;
  return { notes: hit.notes.map((note) => ({ ...note })), exists: hit.exists };
}

function rememberNotes(key: string, notes: CrmNote[], exists: boolean, size: number | null): void {
  const hit: NotesHit = { at: Date.now(), notes: notes.map((note) => ({ ...note })), exists, size };
  notesHits.set(key, hit);
  writeSession(`${NOTES_SESSION}${key}`, hit);
}

function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota pleine : le cache mémoire suffit pour cette session */
  }
}

function dropSessionPrefix(prefix: string): void {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch {
    /* stockage indisponible */
  }
}

export async function listCoffre(path: string, foldersOnly: boolean, ownerId = 0, force = false): Promise<CoffreEntry[]> {
  const key = listCacheKey(path, foldersOnly, ownerId);
  if (!force) {
    const hit = listHits.get(key);
    if (hit && Date.now() - hit.at < LIST_TTL_MS) return hit.rows.map((row) => ({ ...row }));
    const pending = listInflight.get(key);
    if (pending) return pending.then((rows) => rows.map((row) => ({ ...row })));
  } else {
    listHits.delete(key);
    listInflight.delete(key);
  }
  const query = new URLSearchParams({ path, ...coffreZone(ownerId) });
  if (foldersOnly && ownerId <= 0) query.set("zone", "picker");
  if (foldersOnly) query.set("folders_only", "1");
  const run = (async () => {
    const res = await fetch(`${suiteRoot()}/api_suite/coffre-list.php?${query}`, {
      credentials: "include",
      cache: "no-store",
    });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; message?: string; entries?: CoffreEntry[] };
    if (!res.ok || data.success !== true || !Array.isArray(data.entries)) {
      raiseCoffre(res.status, data.error, data.message);
    }
    const entries = data.entries;
    return foldersOnly ? entries.filter((entry) => entry.kind === "folder") : entries;
  })();
  listInflight.set(key, run);
  try {
    const rows = await run;
    listHits.set(key, { at: Date.now(), rows });
    writeSession(`${LIST_SESSION}${key}`, { at: Date.now(), rows });
    return rows.map((row) => ({ ...row }));
  } catch (error) {
    listHits.delete(key);
    throw error;
  } finally {
    listInflight.delete(key);
  }
}

export async function uploadCoffreFile(folder: string, file: File, ownerId = 0, overwrite = false): Promise<string> {
  const body = new FormData();
  body.append("path", folder);
  const zone = coffreZone(ownerId);
  body.append("zone", zone.zone);
  if (zone.owner_id) body.append("owner_id", zone.owner_id);
  if (overwrite) body.append("overwrite", "1");
  body.append("file", file);
  const res = await fetch(`${suiteRoot()}/api_suite/coffre-upload.php`, {
    method: "POST",
    credentials: "include",
    body,
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; message?: string; path?: string };
  if (!res.ok || data.success === false || !data.path) {
    raiseCoffre(res.status, data.error, data.message);
  }
  dropCoffreCache(folder, ownerId);
  return data.path;
}

export async function loadFicheNotes(folder: string, ownerId = 0, force = false): Promise<{ notes: CrmNote[]; exists: boolean }> {
  const key = notesCacheKey(folder, ownerId);
  if (!force) {
    const hit = notesHits.get(key);
    if (hit && Date.now() - hit.at < NOTES_TTL_MS) {
      return { notes: hit.notes.map((note) => ({ ...note })), exists: hit.exists };
    }
    const pending = notesInflight.get(key);
    if (pending) {
      return pending.then((loaded) => ({ notes: loaded.notes.map((note) => ({ ...note })), exists: loaded.exists }));
    }
  } else {
    notesHits.delete(key);
    notesInflight.delete(key);
  }
  const run = loadFicheNotesFresh(folder, ownerId);
  notesInflight.set(key, run);
  try {
    const loaded = await run;
    const size = notesHits.get(key)?.size ?? null;
    notesHits.set(key, { at: Date.now(), notes: loaded.notes, exists: loaded.exists, size });
    writeSession(`${NOTES_SESSION}${key}`, notesHits.get(key));
    return { notes: loaded.notes.map((note) => ({ ...note })), exists: loaded.exists };
  } catch (error) {
    notesHits.delete(key);
    throw error;
  } finally {
    notesInflight.delete(key);
  }
}

async function loadFicheNotesFresh(folder: string, ownerId: number): Promise<{ notes: CrmNote[]; exists: boolean }> {
  const key = notesCacheKey(folder, ownerId);
  const listed = await listCoffre(folder, false, ownerId);
  const file = listed.find((entry) => entry.kind !== "folder" && entry.name === "notes-CRM.md");
  const size = typeof file?.size === "number" ? file.size : null;
  const kept = notesHits.get(key) || readSession<NotesHit>(`${NOTES_SESSION}${key}`);
  if (file && kept && kept.exists && size !== null && size === kept.size) {
    rememberNotes(key, kept.notes, true, size);
    return { notes: kept.notes.map((note) => ({ ...note })), exists: true };
  }
  const current = file ? await readOptional(file.path, ownerId) : null;
  let notes = current ? parseNotes(current) : [];
  if (current && current.trim() && notes.length === 0) {
    notes = [{ id: "fichier", heading: "notes-CRM.md", body: current.trim(), label: "", author: "" }];
  }
  let exists = current !== null;
  if (!exists) {
    const legacyFile = listed.find((entry) => entry.kind !== "folder" && entry.name === "notes.md");
    const previous = legacyFile ? await readOptional(legacyFile.path, ownerId) : null;
    if (legacyFile && previous !== null) {
      notes = parseNotes(previous);
      await saveFicheNotes(folder, notes, false, ownerId);
      exists = true;
      await coffrePost("coffre-delete.php", { path: legacyFile.path, ...coffreZone(ownerId) });
    }
  }
  const olds = listed
    .filter((entry) => entry.kind !== "folder" && /^note-.*\.md$/i.test(entry.name))
    .sort((a, b) => a.name.localeCompare(b.name));
  if (olds.length > 0) {
    for (const entry of olds) {
      const raw = await readOptional(entry.path, ownerId);
      if (!raw) continue;
      const one = splitOne(raw);
      const heading = one.heading || entry.name;
      const marked = readMeta(one.body);
      if (notes.some((note) => note.heading === heading && note.body === marked.body)) continue;
      notes.push({ id: entry.name, heading, body: marked.body, label: marked.label, author: marked.author });
    }
    await saveFicheNotes(folder, notes, exists, ownerId);
    exists = true;
    for (const entry of olds) {
      await coffrePost("coffre-delete.php", { path: entry.path, ...coffreZone(ownerId) });
    }
  }
  rememberNotes(key, notes, exists, exists ? size : null);
  return { notes, exists };
}

export async function moveNotesFile(from: CoffreSpot, to: CoffreSpot): Promise<void> {
  if (from.path === to.path && from.ownerId === to.ownerId) return;
  const source = await listCoffre(from.path, false, from.ownerId);
  if (!source.some((entry) => entry.kind !== "folder" && entry.name === "notes-CRM.md")) return;
  const dest = await listCoffre(to.path, false, to.ownerId);
  const destZone = coffreZone(to.ownerId);
  if (dest.some((entry) => entry.kind !== "folder" && entry.name === "notes-CRM.md")) {
    await coffrePost("coffre-delete.php", { path: notesPath(to.path), ...destZone });
  }
  await coffrePost("coffre-move.php", {
    path: notesPath(from.path),
    dest_parent_path: to.path,
    ...coffreZone(from.ownerId),
    dest_zone: destZone.zone,
    ...(destZone.owner_id ? { dest_owner_id: destZone.owner_id } : {}),
  });
}

export async function saveFicheNotes(folder: string, notes: CrmNote[], exists: boolean, ownerId = 0): Promise<void> {
  const path = notesPath(folder);
  const content = renderNotes(notes).trim();
  const already = await readOptional(path, ownerId);
  if (!content) return;
  if (already === null) {
    const created = await coffrePost("coffre-create-file.php", { parent_path: folder, template: "md", name: "notes-CRM", ...coffreZone(ownerId) });
    if (!created.path) throw new Error("Fichier de notes non créé");
  }
  await coffrePost("coffre-save-text.php", { path, content: `${content}\n`, ...coffreZone(ownerId) });
  rememberNotes(notesCacheKey(folder, ownerId), notes, true, new TextEncoder().encode(`${content}\n`).length);
}

export async function saveJournalFile(folder: string, ownerId: number, content: string): Promise<void> {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  const name = `journal-CRM-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const created = await coffrePost("coffre-create-file.php", { parent_path: folder, template: "md", name, ...coffreZone(ownerId) });
  const path = typeof created.path === "string" && created.path !== "" ? created.path : `${folder ? `${folder}/` : ""}${name}.md`;
  await coffrePost("coffre-save-text.php", { path, content: content.endsWith("\n") ? content : `${content}\n`, ...coffreZone(ownerId) });
}

export function noteStamp(): string {
  return new Date().toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });
}

function notesPath(folder: string): string {
  return folder ? `${folder}/notes-CRM.md` : "notes-CRM.md";
}

export function parseStoredNotes(raw: string): CrmNote[] {
  return parseNotes(raw);
}

/** Sur une fiche reçue : ses notes, plus la note d’une tâche qu’on lui a partagée. `null` : les tâches partagées ne sont pas encore connues, on les cache. */
export function noteVisibleTo(note: CrmNote, viewerId: number, guest: boolean, sharedTaskIds: ReadonlySet<string> | null = null): boolean {
  if (!guest) return true;
  if (viewerId > 0 && (note.authorId || 0) === viewerId) return true;
  return Boolean(note.taskId) && sharedTaskIds !== null && sharedTaskIds.has(note.taskId || "");
}

export function withoutForeignNotes(raw: string, viewerId: number, sharedTaskIds: ReadonlySet<string> | null = null): string {
  const notes = parseNotes(raw).filter((note) => noteVisibleTo(note, viewerId, true, sharedTaskIds));
  return notes.length ? renderNotes(notes) : "";
}

export function withForeignNotes(saved: CrmNote[], originalRaw: string, viewerId: number, sharedTaskIds: ReadonlySet<string> | null = null): CrmNote[] {
  const hidden = parseNotes(originalRaw).filter((note) => !noteVisibleTo(note, viewerId, true, sharedTaskIds));
  if (hidden.length === 0) return saved;
  const kept = new Set(saved.map((note) => `${note.authorId || 0}:${note.heading}`));
  const extra = hidden.filter((note) => !kept.has(`${note.authorId || 0}:${note.heading}`));
  return extra.length ? [...saved, ...extra] : saved;
}

const NOTE_MARK = "<!-- crm-note -->";

function parseNotes(raw: string): CrmNote[] {
  const chunks = raw.includes(NOTE_MARK) ? splitMarked(raw) : splitLegacyNotes(raw);
  return chunks.map((chunk, index) => {
    const one = splitOne(chunk);
    const marked = readMeta(one.body);
    return { id: String(index), heading: one.heading || "Note", body: marked.body, label: marked.label, author: marked.author, authorId: marked.authorId, taskId: marked.taskId, fromTask: marked.fromTask };
  });
}

function splitMarked(raw: string): string[] {
  return raw.split(NOTE_MARK).map((chunk) => chunk.trim()).filter(Boolean);
}

/** Ancien format : une note commence par ### seulement après le séparateur ---. Un titre ### dans le texte reste dans la note. */
function splitLegacyNotes(raw: string): string[] {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const chunks: string[][] = [];
  let current: string[] = [];
  let previous = "";
  let comment = false;
  for (const line of lines) {
    const noteStart = !comment && line.startsWith("### ") && current.some((item) => item.trim() !== "") && previous.trim() === "---";
    if (noteStart) {
      chunks.push(current);
      current = [];
    }
    current.push(line);
    if (line.includes("-->")) comment = false;
    else if (line.includes("<!--")) comment = true;
    if (!comment && line.trim() !== "") previous = line;
  }
  if (current.length > 0) chunks.push(current);
  return chunks.map((chunk) => chunk.join("\n").trim()).filter(Boolean);
}

function renderNotes(notes: CrmNote[]): string {
  return notes
    .map((note) => {
      const by = note.authorId ? `<!-- crm-by:${note.authorId} -->\n\n` : "";
      const who = note.author ? `<!-- crm-author:${note.author.replace(/-->/g, "")} -->\n\n` : "";
      const tag = note.label ? `<!-- crm-label:${note.label} -->\n\n` : "";
      const link = note.taskId ? `<!-- crm-task:${note.taskId.replace(/-->/g, "")} -->\n\n` : "";
      const born = note.fromTask ? "<!-- crm-from:task -->\n\n" : "";
      const shown = note.author ? bodyWithCredit(note.body, note.author) : note.body.trim();
      return `${NOTE_MARK}\n### ${note.heading}\n\n${by}${who}${tag}${link}${born}${shown}\n`;
    })
    .join("\n");
}

function readMeta(body: string): { label: NoteLabel; author: string; authorId: number; taskId: string; fromTask: boolean; body: string } {
  let rest = body.trim();
  let author = "";
  let authorId = 0;
  let taskId = "";
  let fromTask = false;
  let label: NoteLabel = "";
  for (;;) {
    const by = rest.match(/^<!-- crm-by:(\d+) -->\s*/);
    if (by) { authorId = Number(by[1]) || 0; rest = rest.slice(by[0].length).trim(); continue; }
    const who = rest.match(/^<!-- crm-author:([\s\S]*?) -->\s*/);
    if (who) { author = who[1].trim(); rest = rest.slice(who[0].length).trim(); continue; }
    const task = rest.match(/^<!-- crm-task:([\s\S]*?) -->\s*/);
    if (task) { taskId = task[1].trim(); rest = rest.slice(task[0].length).trim(); continue; }
    const born = rest.match(/^<!-- crm-from:task -->\s*/);
    if (born) { fromTask = true; rest = rest.slice(born[0].length).trim(); continue; }
    const mark = rest.match(/^<!-- crm-label:([a-zA-Z0-9_\-]+) -->\s*/);
    if (mark) { label = mark[1] as NoteLabel; rest = rest.slice(mark[0].length).trim(); continue; }
    break;
  }
  if (author && rest.startsWith(`${author}\n`)) rest = rest.slice(author.length).trim();
  const told = rest.match(/^De la part de ([^\n]+)\n*/);
  if (told && !author) author = told[1].trim();
  return { label, author, authorId, taskId, fromTask, body: rest };
}

export function bodyWithCredit(body: string, name: string): string {
  const text = body.replace(/^De la part de [^\n]+\n*/u, "").trim();
  if (!name) return text;
  return `De la part de ${name}\n\n${text}`.trim();
}

export async function keepTaskNote(folder: string, ownerId: number, taskId: string, heading: string, body: string, reuseExisting: boolean): Promise<void> {
  if (!folder) throw new Error("Cette fiche n’a pas de dossier pour ses notes.");
  const loaded = await loadFicheNotes(folder, ownerId);
  const notes = loaded.notes.slice();
  const own = notes.findIndex((item) => item.taskId === taskId);
  const reused = own < 0 && reuseExisting ? notes.findIndex((item) => !item.taskId && noteLine(item.body) === heading) : -1;
  const at = own >= 0 ? own : reused;
  const text = body.trim();
  if (at >= 0) {
    const prev = notes[at];
    notes[at] = {
      ...prev,
      taskId,
      fromTask: Boolean(prev.fromTask) || own >= 0,
      heading: own >= 0 ? heading : prev.heading,
      // Ne jamais retomber sur le titre : sinon « Mes notes » affiche le titre en double.
      body: text || prev.body,
    };
  } else {
    notes.push({ id: taskId, heading, body: text, label: "", author: "", taskId, fromTask: true });
  }
  await saveFicheNotes(folder, notes, loaded.exists, ownerId);
}

/** La clôture d’une tâche semaine barre la note liée. La réouverture retire seulement le marquage « fait ». */
export async function markTaskNoteDone(folder: string, ownerId: number, taskId: string, done: boolean): Promise<void> {
  if (!folder || !taskId) return;
  const loaded = await loadFicheNotes(folder, ownerId);
  const at = loaded.notes.findIndex((item) => item.taskId === taskId);
  if (at < 0) return;
  const prev = loaded.notes[at];
  const label = done ? "done" : (prev.label === "done" || prev.label === "fait" ? "" : prev.label);
  if (prev.label === label) return;
  const notes = loaded.notes.slice();
  notes[at] = { ...prev, label };
  await saveFicheNotes(folder, notes, loaded.exists, ownerId);
}

export async function releaseTaskNote(folder: string, ownerId: number, taskId: string): Promise<void> {
  if (!folder || !taskId) return;
  const loaded = await loadFicheNotes(folder, ownerId);
  const notes = loaded.notes.flatMap((item) => {
    if (item.taskId !== taskId) return [item];
    if (item.fromTask) return [];
    return [{ ...item, taskId: undefined, fromTask: false }];
  });
  if (notes.length === loaded.notes.length && notes.every((item, index) => item.taskId === loaded.notes[index]?.taskId)) return;
  if (notes.length === 0) {
    await coffrePost("coffre-delete.php", { path: notesPath(folder), ...coffreZone(ownerId) });
    return;
  }
  await saveFicheNotes(folder, notes, loaded.exists, ownerId);
}

function noteLine(body: string): string {
  const visible = body.replace(/<!--BLOCKS_JSON[\s\S]*$/, "");
  const line = visible.split("\n").map((part) => part.trim()).find((part) => part && part !== "---" && !part.startsWith("De la part de ")) || "";
  return line.replace(/^#{1,6}\s*/, "").replace(/^[-*]\s+/, "").trim();
}

function stripRule(raw: string): string {
  return raw.replace(/\n+---\s*$/, "").trim();
}

function splitOne(raw: string): { heading: string; body: string } {
  const marked = raw.match(/^#{1,3} (.+)\r?\n+\s*---\s*\r?\n+([\s\S]*)$/);
  if (marked) return { heading: marked[1].trim(), body: stripRule(marked[2]) };
  const title = raw.match(/^#{1,3} (.+)\r?\n+([\s\S]*)$/);
  if (title) return { heading: title[1].trim(), body: stripRule(title[2]) };
  return { heading: "", body: stripRule(raw) };
}

async function readOptional(path: string, ownerId = 0): Promise<string | null> {
  const res = await fetch(coffreFileUrl(path, ownerId), { credentials: "include", cache: "no-store" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Notes illisibles");
  return res.text();
}

const OPEN_EXT = new Set([
  "pdf",
  "jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff", "heic", "svg",
  "txt", "md", "markdown", "mdown", "mkd", "log",
  "doc", "docx", "odt", "rtf", "xls", "xlsx", "xlsm", "ods", "csv", "ppt", "pptx", "odp",
  "eml",
]);

export type CoffreOpenKind = "pdf" | "image" | "text" | "office" | "eml";

export function coffreOpenKind(name: string): CoffreOpenKind | null {
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
  if (ext === "pdf") return "pdf";
  if (ext === "eml") return "eml";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff", "heic", "svg"].includes(ext)) return "image";
  if (["txt", "md", "markdown", "mdown", "mkd", "log"].includes(ext)) return "text";
  if (["doc", "docx", "odt", "rtf", "xls", "xlsx", "xlsm", "ods", "csv", "ppt", "pptx", "odp"].includes(ext)) return "office";
  return null;
}

export function coffreCanShow(entry: CoffreEntry): boolean {
  if (entry.kind === "folder") return true;
  const dot = entry.name.lastIndexOf(".");
  const ext = dot > 0 ? entry.name.slice(dot + 1).toLowerCase() : "";
  return OPEN_EXT.has(ext);
}

export async function saveCoffreText(path: string, content: string, ownerId = 0): Promise<void> {
  await coffrePost("coffre-save-text.php", { path, content, ...coffreZone(ownerId) });
}

export async function createCoffreFolder(parent: string, name: string, ownerId = 0): Promise<string> {
  const created = await coffrePost("coffre-mkdir.php", { parent_path: parent, name, ...coffreZone(ownerId) });
  const path = created.path;
  if (typeof path === "string" && path !== "") return path;
  return parent ? `${parent}/${name}` : name;
}

export function vaultPathFromUrl(value: string): string | null {
  const text = String(value || "");
  const at = text.toLowerCase().indexOf("coffre-download.php");
  if (at < 0) return null;
  const match = text.slice(at).match(/[?&]path=([^&#"'\\\s<>)]+)/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].replace(/\+/g, " "));
  } catch {
    return null;
  }
}

export function mediaPathsIn(source: string, folder: string): string[] {
  const root = folder.replace(/\/+$/, "");
  const found = new Set<string>();
  const re = /coffre-download\.php\?[^"'\\\s<>)]*/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(source))) {
    const path = vaultPathFromUrl(match[0]);
    if (!path || path === root) continue;
    if (root === "" || path.startsWith(`${root}/`)) found.add(path);
  }
  return [...found];
}

function isMarkdownPageName(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith(".md") && !lower.endsWith(".board.md");
}

export async function deletePageAndMedia(path: string, ownerId = 0): Promise<void> {
  const name = path.split("/").pop() || "";
  if (!isMarkdownPageName(name) || name.toLowerCase() === "notes-crm.md") {
    await deleteCoffreItem(path, ownerId);
    return;
  }
  const folder = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  let source = "";
  try {
    source = await readCoffreText(path, ownerId);
  } catch {
    source = "";
  }
  const mine = mediaPathsIn(source, folder).filter((item) => {
    const base = (item.split("/").pop() || "").toLowerCase();
    return base !== "notes-crm.md" && !base.endsWith(".md");
  });
  const keep = new Set<string>();
  try {
    const rows = await listCoffre(folder, false, ownerId, true);
    for (const row of rows) {
      if (row.path === path || row.kind === "folder" || !row.name.toLowerCase().endsWith(".md")) continue;
      let other = "";
      try {
        other = await readCoffreText(row.path, ownerId);
      } catch {
        continue;
      }
      mediaPathsIn(other, folder).forEach((item) => keep.add(item));
    }
  } catch {
    keep.clear();
    mine.length = 0;
  }
  await deleteCoffreItem(path, ownerId);
  for (const item of mine) {
    if (keep.has(item)) continue;
    try {
      await deleteCoffreItem(item, ownerId);
    } catch {
      /* déjà absent ou verrouillé */
    }
  }
}

export async function releaseMediaFile(path: string, folder: string, ownerId = 0, skipPage = ""): Promise<boolean> {
  const root = folder.replace(/\/+$/, "");
  const base = (path.split("/").pop() || "").toLowerCase();
  if (!path || base === "notes-crm.md" || base.endsWith(".md")) return false;
  if (root !== "" && !path.startsWith(`${root}/`)) return false;
  try {
    const rows = await listCoffre(root, false, ownerId, true);
    for (const row of rows) {
      if (row.path === skipPage || row.kind === "folder" || !row.name.toLowerCase().endsWith(".md")) continue;
      let text = "";
      try {
        text = await readCoffreText(row.path, ownerId);
      } catch {
        continue;
      }
      if (mediaPathsIn(text, root).includes(path)) return false;
    }
  } catch {
    return false;
  }
  await deleteCoffreItem(path, ownerId);
  return true;
}

export async function copyVaultFile(path: string, name: string, ownerId = 0): Promise<string> {
  const lower = name.toLowerCase();
  if (lower.endsWith(".md") || lower.endsWith(".txt") || lower.endsWith(".csv")) return duplicateVaultFile(path, name, ownerId);
  const res = await fetch(coffreFileUrl(path, ownerId), { credentials: "include", cache: "no-store" });
  if (!res.ok) throw new Error("Duplication impossible.");
  const blob = await res.blob();
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  return uploadCoffreFile(parent, new File([blob], `${stem} copie${ext}`, { type: blob.type || "application/octet-stream" }), ownerId);
}

export async function duplicateVaultFile(path: string, name: string, ownerId = 0): Promise<string> {
  const raw = await readCoffreText(path, ownerId);
  const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  const board = name.toLowerCase().endsWith(".board.md");
  const stem = board ? name.replace(/\.board\.md$/i, "") : name.replace(/\.md$/i, "");
  return createCoffreMarkdown(parent, board ? `${stem} copie.board` : `${stem} copie`, raw, ownerId);
}

export async function createCoffreMarkdown(parent: string, name: string, content: string, ownerId = 0): Promise<string> {
  const created = await coffrePost("coffre-create-file.php", { parent_path: parent, template: "md", name, ...coffreZone(ownerId) });
  const path = typeof created.path === "string" && created.path !== "" ? created.path : `${parent ? `${parent}/` : ""}${name}.md`;
  if (content) await saveCoffreText(path, content, ownerId);
  return path;
}

export async function deleteCoffreItem(path: string, ownerId = 0): Promise<void> {
  await coffrePost("coffre-delete.php", { path, ...coffreZone(ownerId) });
}

export async function moveCoffreItem(path: string, destParent: string, ownerId = 0): Promise<void> {
  const zone = coffreZone(ownerId);
  await coffrePost("coffre-move.php", {
    path,
    dest_parent_path: destParent,
    ...zone,
    dest_zone: zone.zone,
    ...(zone.owner_id ? { dest_owner_id: zone.owner_id } : {}),
  });
}

export async function renameCoffreItem(path: string, newName: string, ownerId = 0, crmManaged = false): Promise<string> {
  const data = await coffrePost("coffre-rename.php", { path, new_name: newName, ...coffreZone(ownerId), ...(crmManaged ? { crm_managed: true } : {}) });
  if (typeof data.path === "string" && data.path !== "") return data.path;
  const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  return parent ? `${parent}/${newName}` : newName;
}

export async function officeConfig(path: string, mode: "view" | "edit", ownerId = 0): Promise<{ document_server_url: string; config: Record<string, unknown> }> {
  const data = await coffrePost("coffre-onlyoffice-config.php", { path, ...coffreZone(ownerId), mode, editor_type: "desktop" });
  const url = String(data.document_server_url || "");
  const config = data.config;
  if (!url || !config || typeof config !== "object") throw new Error("Éditeur document indisponible.");
  return { document_server_url: url, config: config as Record<string, unknown> };
}

export async function readCoffreText(path: string, ownerId = 0): Promise<string> {
  const res = await fetch(coffreFileUrl(path, ownerId), { credentials: "include", cache: "no-store" });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    raiseCoffre(res.status, data.error, data.message || "Note illisible");
  }
  return res.text();
}

async function coffrePost(script: string, body: Record<string, string>): Promise<Record<string, unknown>> {
  const res = await fetch(`${suiteRoot()}/api_suite/${script}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; message?: string; path?: string };
  if (!res.ok || data.success === false) {
    raiseCoffre(res.status, data.error, data.message);
  }
  if (MUTATING_COFFRE.has(script)) {
    const owner = Number(body.owner_id || 0);
    const target = body.path || body.parent_path || body.dest_parent_path || "";
    dropCoffreCache(target || undefined, owner);
  }
  return data;
}

export function coffreFileUrl(path: string, ownerId = 0): string {
  const query = new URLSearchParams({ path, ...coffreZone(ownerId) });
  return `${suiteRoot()}/api_suite/coffre-download.php?${query}`;
}

function coffreError(status: number, error?: string, message?: string): string {
  if (error === "coffre_locked" || error === "coffre_session_expired") return "Le Coffre est fermé. Saisissez votre mot de passe maître.";
  if (error === "item_protected" || (message || "").includes("Cet élément est verrouillé")) return "Cet élément est verrouillé. Rendez-vous dans votre Coffre et saisissez votre mot de passe maître pour y accéder.";
  if (error === "share_master_required") return "Ce partage est verrouillé. Rendez-vous dans votre Coffre et saisissez votre mot de passe maître pour y accéder.";
  if (error === "share_forbidden") return message || "Ce partage ne vous est pas ouvert.";
  if (status === 403 && !message) return "La tuile Coffre n’est pas ouverte pour ce compte.";
  return message || "Le Coffre ne répond pas.";
}
